// Grid/EnhancedTableHead.js
import React from 'react';
import PropTypes from 'prop-types';
import { TableHead, TableRow, TableCell, Checkbox, TableSortLabel, Box } from '@mui/material';
import { visuallyHidden } from '@mui/utils';

/**
 * EnhancedTableHead component for displaying a sortable table header.
 *
 * @component
 * @param {Object} props Component props.
 * @param {function} props.onSelectAllClick Callback fired when the select-all checkbox is clicked.
 * @param {('asc'|'desc')} props.order The sort order.
 * @param {string} props.orderBy The column id currently being sorted.
 * @param {number} props.numSelected The number of selected rows.
 * @param {number} props.rowCount The total number of rows.
 * @param {function} props.onRequestSort Callback fired when a column header is clicked for sorting.
 * @param {Array<Object>} props.headCells Array of objects representing the table's head cells.
 * @returns {React.Element} The EnhancedTableHead component.
 */
function EnhancedTableHead(props) {
	const {
		onSelectAllClick,
		order,
		orderBy,
		numSelected,
		rowCount,
		onRequestSort,
		headCells
	} = props;
	const createSortHandler = (property) => (event) => {
		onRequestSort(event, property);
	};

	return (
		<TableHead>
			<TableRow>
				<TableCell padding="checkbox">
					<Checkbox
						color="primary"
						indeterminate={numSelected > 0 && numSelected < rowCount}
						checked={rowCount > 0 && numSelected === rowCount}
						onChange={onSelectAllClick}
						inputProps={{
							'aria-label': 'select all items',
						}}
					/>
				</TableCell>
				{headCells.map((headCell) => (
					<TableCell
						key={headCell.id}
						align={headCell.numeric ? 'right' : 'left'}
						padding={headCell.disablePadding ? 'none' : 'normal'}
						sortDirection={orderBy === headCell.id ? order : false}
					>
						<TableSortLabel
							active={orderBy === headCell.id}
							direction={orderBy === headCell.id ? order : 'asc'}
							onClick={createSortHandler(headCell.id)}
						>
							{headCell.label}
							{orderBy === headCell.id ? (
								<Box component="span" sx={visuallyHidden}>
									{order === 'desc' ? 'sorted descending' : 'sorted ascending'}
								</Box>
							) : null}
						</TableSortLabel>
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
}

EnhancedTableHead.propTypes = {
	onSelectAllClick: PropTypes.func.isRequired,
	order: PropTypes.oneOf(['asc', 'desc']).isRequired,
	orderBy: PropTypes.string.isRequired,
	numSelected: PropTypes.number.isRequired,
	rowCount: PropTypes.number.isRequired,
	onRequestSort: PropTypes.func.isRequired,
	headCells: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.string.isRequired,
		numeric: PropTypes.bool,
		disablePadding: PropTypes.bool,
		label: PropTypes.string.isRequired
	})).isRequired,
};

export default EnhancedTableHead;
